import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { useEffect } from 'react';

const HowItWorksSection = ({ setActiveSection }: HowItWorksSectionProps) => {
  const { ref, inView } = useInView({ 
    threshold: 0.2,
    triggerOnce: false 
  });

  useEffect(() => {
    if (inView) {
      setActiveSection('how-it-works');
    }
  }, [inView, setActiveSection]);

  const steps = [
    {
      number: '01',
      title: 'Consultation',
      description: 'Share your vision and goals with our team',
      icon: 'ri-discuss-line'
    },
    {
      number: '02',
      title: 'Strategy',
      description: 'We develop a tailored creative approach',
      icon: 'ri-lightbulb-line'
    },
    {
      number: '03',
      title: 'Creation',
      description: 'AI and human expertise bring ideas to life',
      icon: 'ri-magic-line'
    },
    {
      number: '04',
      title: 'Delivery',
      description: 'Receive polished, ready-to-use content',
      icon: 'ri-rocket-line'
    }
  ];

  return (
    <section
      id="how-it-works"
      ref={ref}
      className="relative py-16 sm:py-20 lg:py-32 px-4 sm:px-6 lg:px-12 bg-black"
    >
      <div className="max-w-[1600px] mx-auto">
        {/* Title */}
        <div className="text-center mb-12 sm:mb-16 lg:mb-20">
          <p className="text-xs sm:text-sm uppercase tracking-[3px] text-brand-aqua mb-3 font-semibold">
            OUR PROCESS
          </p>
          <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-tight px-4">
            How It Works
          </h2>
        </div>

        {/* Vertical Timeline for Mobile */}
        <div className="space-y-8 sm:space-y-0 sm:grid sm:grid-cols-2 lg:grid-cols-4 sm:gap-6 lg:gap-8">
          {steps.map((step, index) => (
            <motion.div
              key={step.number}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: index * 0.1 }}
              className="relative"
            >
              {/* Mobile: Vertical Timeline */}
              <div className="sm:hidden flex gap-6">
                {/* Timeline Line */}
                <div className="flex flex-col items-center">
                  <div className="w-16 h-16 flex items-center justify-center bg-gradient-to-br from-brand-pink to-brand-aqua rounded-2xl flex-shrink-0">
                    <i className={`${step.icon} text-2xl text-white`}></i>
                  </div>
                  {index < steps.length - 1 && (
                    <div className="w-0.5 h-full bg-gradient-to-b from-brand-aqua/50 to-transparent mt-4" />
                  )}
                </div>

                {/* Content */}
                <div className="flex-1 pb-8">
                  <div className="text-5xl font-bold text-white/10 mb-2">{step.number}</div>
                  <h3 className="text-2xl font-bold text-white mb-3 leading-tight">
                    {step.title}
                  </h3>
                  <p className="text-base text-white/70 leading-relaxed">
                    {step.description}
                  </p>
                </div>
              </div>

              {/* Desktop: Original Grid */}
              <div className="hidden sm:block text-center">
                <div className="w-16 h-16 sm:w-20 sm:h-20 flex items-center justify-center bg-gradient-to-br from-brand-pink to-brand-aqua rounded-2xl mx-auto mb-6">
                  <i className={`${step.icon} text-3xl sm:text-4xl text-white`}></i>
                </div>
                <div className="text-5xl sm:text-6xl font-bold text-white/10 mb-4">{step.number}</div>
                <h3 className="text-xl sm:text-2xl font-bold text-white mb-3 leading-tight">
                  {step.title}
                </h3>
                <p className="text-sm sm:text-base text-white/70 leading-relaxed">
                  {step.description}
                </p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default HowItWorksSection;
